import _ from 'lodash';
import { type FC, useMemo } from 'react';
import { MultiSelect } from 'src/components/form/controlled-multiselect/ControlledMultiselect';
import { useDepartmentOptions } from 'src/components/form/department-selector/useDepartmentOptions';

import type { CustomAttributeProps } from './CustomAttributeProps';

export const CustomAttributeDepartmentMultiSelect: FC<
  CustomAttributeProps<null | string[]>
> = ({ label, onChange, value, disabled, error, description }) => {
  // CUS-234: share the standard Departments field's option builder so a custom
  // department field presents its departments under the same group headings,
  // sorted the same way, instead of as a flat list. The hook also collapses to a
  // flat list when no department has a group, so an org that uses none sees no
  // stray heading.
  const { departments, optionItems } = useDepartmentOptions();

  // Options are now nested under group headings, so the current selection has to
  // be matched against the flattened leaves rather than the top-level list.
  const selectedOptions = useMemo(() => {
    const flatOptions = optionItems.flatMap((option) =>
      'options' in option ? (option.options ?? []) : [option]
    );

    return flatOptions.filter(
      (option) => option.value != null && value?.includes(option.value)
    );
  }, [optionItems, value]);

  return (
    <MultiSelect
      testId={label}
      description={description}
      label={label}
      onChange={(e) => onChange(e.detail.selectedOptions.map((c) => c.value!))}
      expandToViewport
      options={optionItems}
      disabled={disabled}
      placeholder={'Select'}
      errorMessage={error}
      tokenSection={<></>}
      selectedOptions={selectedOptions}
      renderTokens={false}
      previewChangesFormatter={(
        val: [{ UserId?: string; value?: string }] | null | undefined
      ) => {
        if (Array.isArray(val)) {
          return val
            .map((v) => v)
            .map((v) => departments.find((d) => d.DepartmentTypeId === v)?.Name)
            .join(', ');
        }

        return '-';
      }}
      hasFieldChanged={(
        val:
          | {
              from: { UserId?: string; value?: string }[];
              to: { UserId?: string; value?: string }[];
            }
          | null
          | undefined
      ) => {
        if (val === undefined || val === null) {
          return false;
        }
        const from = val.from?.map((v) => v).sort();
        const to = val.to?.map((v) => v).sort();

        return !_.isEqual(from, to);
      }}
    />
  );
};
